"use client";

import React from "react";
import { motion } from "framer-motion";
import * as Icon from "@phosphor-icons/react/dist/ssr";

const steps = [
  { id: 1, label: "Panier", href: "/cart" },
  { id: 2, label: "Livraison & Paiement", href: null },
  { id: 3, label: "Confirmation", href: null },
] as const;

const container = {
  hidden: { opacity: 0, y: 12 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      staggerChildren: 0.08,
      delayChildren: 0.1,
    },
  },
};

const item = {
  hidden: { opacity: 0, y: 8 },
  visible: { opacity: 1, y: 0 },
};

type CheckoutStepsProps = {
  currentStep: 1 | 2 | 3;
  className?: string;
};

export function CheckoutSteps({ currentStep, className = "" }: CheckoutStepsProps) {
  return (
    <motion.div
      className={`flex items-center justify-center mb-6 md:mb-8 px-2 ${className}`}
      initial="hidden"
      animate="visible"
      variants={container}
      aria-label="Étapes de la commande"
    >
      <nav
        className="flex items-center w-full max-w-2xl bg-white/80 backdrop-blur-sm rounded-2xl border border-gray-200/80 shadow-sm py-4 px-4 md:py-5 md:px-6"
        aria-label="Étapes de la commande"
      >
        <div className="flex items-center w-full gap-0 md:gap-2">
          {steps.map((step, index) => {
            const isCompleted = step.id < currentStep;
            const isCurrent = step.id === currentStep;
            const isUpcoming = step.id > currentStep;
            const isFirst = index === 0;
            const isLast = index === steps.length - 1;

            return (
              <React.Fragment key={step.id}>
                {/* Step node */}
                <motion.div
                  className="flex items-center gap-2 flex-shrink-0"
                  variants={item}
                >
                  {isCompleted && step.href ? (
                    <a
                      href={step.href}
                      className="flex items-center gap-2 text-gray-500 hover:text-pink transition-colors duration-200 group"
                      aria-label={`Étape ${step.id} terminée : ${step.label}`}
                    >
                      <motion.span
                        className="w-9 h-9 md:w-10 md:h-10 rounded-full bg-gray-100 flex items-center justify-center text-gray-600 group-hover:bg-pink/10 transition-colors duration-200"
                        whileHover={{ scale: 1.08 }}
                        whileTap={{ scale: 0.96 }}
                      >
                        <motion.span
                          initial={{ scale: 0 }}
                          animate={{ scale: 1 }}
                          transition={{ type: "spring", stiffness: 400, damping: 15 }}
                        >
                          <Icon.Check size={18} weight="bold" />
                        </motion.span>
                      </motion.span>
                      <span className="text-xs md:text-sm font-medium hidden sm:inline max-w-[100px] md:max-w-none truncate">
                        {step.label}
                      </span>
                    </a>
                  ) : isCompleted ? (
                    <div className="flex items-center gap-2 text-gray-500 flex-shrink-0">
                      <motion.span
                        className="w-9 h-9 md:w-10 md:h-10 rounded-full bg-gray-100 flex items-center justify-center text-gray-600"
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{ type: "spring", stiffness: 400, damping: 15 }}
                      >
                        <Icon.Check size={18} weight="bold" />
                      </motion.span>
                      <span className="text-xs md:text-sm font-medium hidden sm:inline max-w-[100px] md:max-w-none truncate">
                        {step.label}
                      </span>
                    </div>
                  ) : isCurrent ? (
                    <div
                      className="flex items-center gap-2 flex-shrink-0"
                      aria-current="step"
                    >
                      <motion.span
                        className="w-9 h-9 md:w-10 md:h-10 rounded-full bg-pink text-white flex items-center justify-center text-sm md:text-base font-bold shadow-lg shadow-pink/30"
                        animate={{
                          boxShadow: [
                            "0 4px 14px rgba(255, 67, 112, 0.35)",
                            "0 4px 20px rgba(255, 67, 112, 0.5)",
                            "0 4px 14px rgba(255, 67, 112, 0.35)",
                          ],
                        }}
                        transition={{
                          duration: 2,
                          repeat: Infinity,
                          ease: "easeInOut",
                        }}
                        whileHover={{ scale: 1.05 }}
                      >
                        {step.id}
                      </motion.span>
                      <span className="text-xs md:text-sm font-semibold text-pink hidden sm:inline max-w-[100px] md:max-w-none truncate">
                        {step.label}
                      </span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 text-gray-300 flex-shrink-0">
                      <motion.span
                        className="w-9 h-9 md:w-10 md:h-10 rounded-full border-2 border-gray-200 flex items-center justify-center text-sm font-bold text-gray-400"
                        variants={item}
                      >
                        {step.id}
                      </motion.span>
                      <span className="text-xs md:text-sm font-medium hidden sm:inline max-w-[100px] md:max-w-none truncate text-gray-400">
                        {step.label}
                      </span>
                    </div>
                  )}
                </motion.div>

                {/* Connector line - fills when next step is reached */}
                {!isLast && (
                  <div className="flex-1 flex items-center min-w-[16px] md:min-w-[24px] mx-0.5 md:mx-1">
                    <div className="w-full h-1 rounded-full bg-gray-200 overflow-hidden">
                      <motion.div
                        className="h-full rounded-full bg-pink"
                        initial={{ width: "0%" }}
                        animate={{
                          width: currentStep > step.id ? "100%" : "0%",
                        }}
                        transition={{
                          duration: 0.5,
                          delay: 0.2 + index * 0.1,
                          ease: "easeOut",
                        }}
                      />
                    </div>
                  </div>
                )}
              </React.Fragment>
            );
          })}
        </div>
      </nav>
    </motion.div>
  );
}
